import { z } from 'zod';
import { BILLING_INTERVALS, PLAN_KINDS } from '@bulk/types';
import { currencySchema, idSchema, patchOf, timeHHMM, weekdaySchema } from './common';

export const accessRulesSchema = z.object({
  allowedWeekdays: z.array(weekdaySchema).default(['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN']),
  allowedFrom: timeHHMM.nullable().default(null),
  allowedUntil: timeHHMM.nullable().default(null),
  dailyVisitLimit: z.number().int().min(1).nullable().default(null),
  totalVisitLimit: z.number().int().min(1).nullable().default(null),
  accessZoneIds: z.array(idSchema).default([]),
  locationIds: z.array(idSchema).default([]).describe('The branches the plan opens; empty means every branch'),
});
export type AccessRules = z.infer<typeof accessRulesSchema>;

export const planPauseRulesSchema = z.object({
  allowed: z.boolean().default(true),
  maxPauseDays: z.number().int().min(1).max(365).nullable().default(null),
  maxPauses: z.number().int().min(0).max(12).nullable().default(null),
  requiresApproval: z.boolean().nullable().default(null),
});
export type PlanPauseRules = z.infer<typeof planPauseRulesSchema>;

export const planVisibilitySchema = z.enum(['PUBLIC', 'HIDDEN']);

export const createPlanSchema = z.object({
  name: z.string().trim().min(2).max(120),
  nameLocalized: z.record(z.string(), z.string()).default({}),
  description: z.string().trim().max(2000).nullable().default(null),
  descriptionLocalized: z.record(z.string(), z.string()).default({}),
  priceMinor: z.number().int().nonnegative(),
  currency: currencySchema.optional(),
  durationDays: z.number().int().min(1).max(3660),
  billingInterval: z.enum(BILLING_INTERVALS).default('ONE_TIME'),
  accessRules: accessRulesSchema.default(() => accessRulesSchema.parse({})),
  classAccess: z.boolean().default(true),
  bookingLimitPerWeek: z.number().int().min(1).nullable().default(null),
  pauseRules: planPauseRulesSchema.default(() => planPauseRulesSchema.parse({})),
  autoRenew: z.boolean().default(false),
  visibility: planVisibilitySchema.default('PUBLIC'),
  purchasable: z.boolean().default(true),
  sortOrder: z.number().int().default(0),
  kind: z
    .enum(PLAN_KINDS)
    .default('MEMBERSHIP')
    .describe('SESSION_PACKAGE sells sessionCount sessions, valid for durationDays, of one appointment type or, as a class pack, of the classes in classIds; it grants no membership'),
  appointmentTypeId: idSchema.nullable().default(null).describe('The appointment type a session package is for; null for a membership or a class pack'),
  sessionCount: z.number().int().min(1).max(500).nullable().default(null),
  classIds: z.array(idSchema).max(50).default([]).describe('A class pack: the classes each of its visits books. Empty for a membership or an appointment package'),
  importKey: z
    .string()
    .trim()
    .regex(/^[a-z0-9][a-z0-9:._-]{0,119}$/)
    .optional()
    .describe('Where an imported plan came from, such as goldsgym:12, so running the import again updates it. Unique in the gym; set only when the plan is created'),
});
export type CreatePlanInput = z.infer<typeof createPlanSchema>;

export const updatePlanSchema = patchOf(createPlanSchema.omit({ importKey: true })).extend({
  archived: z.boolean().optional(),
});
export type UpdatePlanInput = z.infer<typeof updatePlanSchema>;

export const planSchema = z.object({
  id: idSchema,
  gymId: idSchema,
  name: z.string(),
  nameLocalized: z.record(z.string(), z.string()),
  description: z.string().nullable(),
  descriptionLocalized: z.record(z.string(), z.string()),
  priceMinor: z.number().int(),
  currency: z.string(),
  durationDays: z.number().int(),
  billingInterval: z.enum(BILLING_INTERVALS),
  accessRules: accessRulesSchema,
  classAccess: z.boolean(),
  bookingLimitPerWeek: z.number().int().nullable(),
  pauseRules: planPauseRulesSchema,
  autoRenew: z.boolean(),
  visibility: planVisibilitySchema,
  purchasable: z.boolean(),
  sortOrder: z.number().int(),
  kind: z.enum(PLAN_KINDS),
  appointmentTypeId: idSchema.nullable(),
  sessionCount: z.number().int().nullable(),
  classIds: z.array(idSchema),
  importKey: z.string().nullable(),
  archivedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type PlanDto = z.infer<typeof planSchema>;

export const publicPlanSchema = planSchema
  .pick({
    id: true,
    name: true,
    nameLocalized: true,
    description: true,
    descriptionLocalized: true,
    priceMinor: true,
    currency: true,
    durationDays: true,
    billingInterval: true,
    classAccess: true,
    purchasable: true,
    autoRenew: true,
    sortOrder: true,
    kind: true,
    sessionCount: true,
    classIds: true,
  })
  .extend({
    validAt: z.array(z.string()).describe('Names of the branches the plan opens; empty when it opens every branch'),
    locationIds: z.array(idSchema).describe('The open branches the plan opens, as ids of `locations`; empty when it opens every branch'),
    listPriceMinor: z.number().int(),
    discountPct: z.number().int(),
  });
export type PublicPlanDto = z.infer<typeof publicPlanSchema>;

export const planEditorsSchema = z.object({
  marketing: z.boolean().describe('Whether marketing may create and edit plans (the MARKETING ability plans.edit)'),
  canSwitch: z.boolean().describe('Whether the caller may change that: the gym admin, or a real owner'),
});
export type PlanEditorsDto = z.infer<typeof planEditorsSchema>;

export const setPlanEditorsSchema = z.object({ marketing: z.boolean() });
export type SetPlanEditorsInput = z.infer<typeof setPlanEditorsSchema>;
